import { apiRequest, ApiResponse } from './api';
import { Contract } from '../types';

export interface ContractFilters {
  status?: string;
  dia_vencimento?: number;
  fidelidade?: number;
  isento?: boolean;
  sms_lembrete?: boolean;
  gerar_fatura_automatico?: boolean;
  geranfcom?: boolean;
  pgcorte?: boolean;
  dias_corte?: number;
  acrescimo?: number;
  desconto?: number;
  gateway_id?: number;
  cfop?: string;
  modalidade?: string;
  vendedor_id?: number;
  plano_id?: number;
}

export interface ContractListParams {
  page?: number;
  limit?: number;
  filters?: ContractFilters;
}

export interface ContractCreateData {
  titulo: string;
  cliente_id: number;
  escritorio_id: number;
  status: 'ativo' | 'bloqueado' | 'cancelado' | 'aguardando_instalacao' | 'aguardando_assinatura';
  data_contrato: string;
  dia_vencimento: number;
  fidelidade: number;
  isento: boolean;
  sms_lembrete: boolean;
  gerar_fatura_automatico: boolean;
  geranfcom: boolean;
  pgcorte: boolean;
  dias_corte: number;
  tx_instalacao: number;
  acrescimo: number;
  desconto: number;
  cep_inst: string;
  cidade_inst: string;
  estado_inst: string;
  bairro_inst: string;
  endereco_inst: string;
  numero_inst: string;
  complemento_inst?: string;
  referencia_inst?: string;
  nas_server?: number | null;
  data_bloqueio?: string;
  data_cancelamento?: string;
  data_instalacao: string;
  template?: string;
  servico_id?: number; // ID do serviço para transferir
}

export interface ContractUpdateData extends Partial<ContractCreateData> { }

export interface BairroInstalacaoOption {
  bairro: string;
  quantidade: number;
  cidade_inst: string;
  estado_inst: string;
}

class ContractService {
  private baseUrl = '/contratos';

  // Listar todos os contratos
  async getAll(params: ContractListParams = {}): Promise<ApiResponse<Contract[]>> {
    const { page = 1, limit = 10, filters = {} } = params;

    const queryParams = this.buildQueryParams({ page, limit, filters });

    return apiRequest.get<Contract[]>(`${this.baseUrl}?${queryParams}`);
  }

  /** Bairros distintos de instalação cadastrados nos contratos. */
  async getBairrosInstalacao(params?: {
    cidade?: string;
    estado?: string;
    search?: string;
    escritorio_id?: number;
  }): Promise<BairroInstalacaoOption[]> {
    const query = new URLSearchParams();
    if (params?.cidade?.trim()) query.set('cidade', params.cidade.trim());
    if (params?.estado?.trim()) query.set('estado', params.estado.trim());
    if (params?.search?.trim()) query.set('search', params.search.trim());
    if (params?.escritorio_id) query.set('escritorio_id', String(params.escritorio_id));
    const qs = query.toString();
    const response = await apiRequest.get<BairroInstalacaoOption[]>(
      `${this.baseUrl}/bairros-instalacao${qs ? `?${qs}` : ''}`
    );
    return response.data ?? [];
  }

  // Listar contratos de um cliente
  async getByClientId(
    clientId: number,
    params: ContractListParams = {}
  ): Promise<ApiResponse<Contract[]>> {
    const { page = 1, limit = 25, filters = {} } = params;

    const queryParams = this.buildQueryParams({ page, limit, filters });

    return apiRequest.get<Contract[]>(`${this.baseUrl}/client/${clientId}?${queryParams}`);
  }

  // Obter contrato por ID
  async getById(id: number): Promise<ApiResponse<Contract>> {
    return apiRequest.get<Contract>(`${this.baseUrl}/${id}`);
  }

  // Criar novo contrato
  async create(data: ContractCreateData): Promise<ApiResponse<Contract>> {
    return apiRequest.post<Contract>(this.baseUrl, data);
  }

  // Atualizar contrato
  async update(id: number, data: ContractUpdateData): Promise<ApiResponse<Contract>> {
    return apiRequest.put<Contract>(`${this.baseUrl}/${id}`, data);
  }

  // Atualizar múltiplos contratos
  async bulkUpdate(ids: number[], data: Partial<ContractUpdateData>): Promise<ApiResponse<{ count: number }>> {
    return apiRequest.put<{ count: number }>(`${this.baseUrl}/bulk-update`, { ids, data });
  }

  // Deletar contrato
  async delete(id: number): Promise<ApiResponse<void>> {
    return apiRequest.delete<void>(`${this.baseUrl}/${id}`);
  }

  // Obter estatísticas dos contratos
  async getStats(filters: ContractFilters = {}): Promise<ApiResponse<{
    total: number;
    ativos: number;
    bloqueados: number;
    cancelados: number;
    aguardando_instalacao: number;
    aguardando_assinatura: number;
    com_faturas_vencidas: number;
    sem_faturas: number;
  }>> {
    const queryParams = this.buildQueryParams({ filters });
    const queryString = queryParams.toString();
    const url = `${this.baseUrl}/stats${queryString ? `?${queryString}` : ''}`;

    return apiRequest.get<{
      total: number;
      ativos: number;
      bloqueados: number;
      cancelados: number;
      aguardando_instalacao: number;
      aguardando_assinatura: number;
      com_faturas_vencidas: number;
      sem_faturas: number;
    }>(url);
  }

  // Alterar status do contrato
  async updateStatus(
    id: number,
    status: 'ativo' | 'bloqueado' | 'cancelado'
  ): Promise<ApiResponse<Contract>> {
    return this.update(id, { status });
  }

  // Bloquear contrato
  async bloquear(id: number): Promise<ApiResponse<Contract>> {
    return apiRequest.patch<Contract>(`${this.baseUrl}/${id}/bloquear`);
  }

  // Desbloquear contrato
  async desbloquear(id: number): Promise<ApiResponse<Contract>> {
    return apiRequest.patch<Contract>(`${this.baseUrl}/${id}/desbloquear`);
  }

  // Desbloquear contrato em confiança
  async desbloquearConfianca(id: number, dataBloqueio?: string): Promise<ApiResponse<Contract>> {
    const data = dataBloqueio ? { data_bloqueio: dataBloqueio } : {};
    return apiRequest.patch<Contract>(`${this.baseUrl}/${id}/desbloquear-confianca`, data);
  }

  // Cancelar contrato
  async cancelar(id: number, motivo: string): Promise<ApiResponse<Contract>> {
    return apiRequest.patch<Contract>(`${this.baseUrl}/${id}/cancelar`, { motivo });
  }

  //ativar contrato
  async ativar(id: number): Promise<ApiResponse<Contract>> {
    return apiRequest.patch<Contract>(`${this.baseUrl}/${id}/ativar`);
  }

  // Suspender contrato
  async suspender(id: number, data: {
    suspensao_inicio: string;
    suspensao_fim?: string | null;
    suspensao_motivo: string;
    observacao?: string;
  }): Promise<ApiResponse<Contract>> {
    return apiRequest.patch<Contract>(`${this.baseUrl}/${id}/suspender`, data);
  }

  async getContractContent(id: number): Promise<ApiResponse<string>> {
    return apiRequest.get<string>(`${this.baseUrl}/${id}/content`);
  }

  // Alterar titularidade do contrato
  async alterarTitularidade(id: number, data: {
    novo_cliente_id: number;
    migrar_titulos_abertos: boolean;
    migrar_titulos_pagos_cancelados: boolean;
    migrar_notas_nfcom: boolean;
    migrar_chamados: boolean;
  }): Promise<ApiResponse<Contract>> {
    return apiRequest.patch<Contract>(`${this.baseUrl}/${id}/alterar-titularidade`, data);
  }

  // Validar dados do contrato antes de enviar
  validateContractData(data: ContractCreateData | ContractUpdateData): string[] {
    const errors: string[] = [];

    if ('titulo' in data && (!data.titulo || data.titulo.trim().length === 0)) {
      errors.push('Título é obrigatório');
    }

    if ('titulo' in data && data.titulo && data.titulo.length > 255) {
      errors.push('Título deve ter no máximo 255 caracteres');
    }

    if ('dia_vencimento' in data && data.dia_vencimento) {
      if (data.dia_vencimento < 1 || data.dia_vencimento > 31) {
        errors.push('Dia de vencimento deve ser entre 1 e 31');
      }
    }

    if ('fidelidade' in data && data.fidelidade !== undefined && data.fidelidade < 0) {
      errors.push('Fidelidade não pode ser negativa');
    }

    if ('dias_corte' in data && data.dias_corte !== undefined && data.dias_corte < 0) {
      errors.push('Dias para corte não pode ser negativo');
    }

    if ('tx_instalacao' in data && data.tx_instalacao !== undefined && data.tx_instalacao < 0) {
      errors.push('Taxa de instalação não pode ser negativa');
    }

    if ('acrescimo' in data && data.acrescimo !== undefined && data.acrescimo < 0) {
      errors.push('Acréscimo não pode ser negativo');
    }

    if ('desconto' in data && data.desconto !== undefined && data.desconto < 0) {
      errors.push('Desconto não pode ser negativo');
    }

    if ('estado_inst' in data && data.estado_inst && data.estado_inst.length !== 2) {
      errors.push('Estado deve ter 2 caracteres');
    }

    return errors;
  }

  // Helper para construir query params
  private buildQueryParams(params: { page?: number; limit?: number; filters?: ContractFilters }): URLSearchParams {
    const { page, limit, filters = {} } = params;
    const queryParams = new URLSearchParams();

    if (page !== undefined) queryParams.append('page', page.toString());
    if (limit !== undefined) queryParams.append('limit', limit.toString());

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    return queryParams;
  }

  // Formatar dados para exibição
  formatContractForDisplay(contract: Contract) {
    return {
      ...contract,
      status_label: this.getStatusLabel(contract.status),
      tx_instalacao_formatted: this.formatCurrency(contract.tx_instalacao),
      acrescimo_formatted: `${contract.acrescimo}%`,
      desconto_formatted: `${contract.desconto}%`,
      endereco_completo: this.formatEnderecoCompleto(contract),
    };
  }

  // Obter label do status
  private getStatusLabel(status: string): string {
    const labels = {
      ativo: 'Ativo',
      bloqueado: 'Bloqueado',
      cancelado: 'Cancelado',
      aguardando_instalacao: 'Aguardando Instalação',
      aguardando_assinatura: 'Aguardando Assinatura',
    };
    return labels[status as keyof typeof labels] || status;
  }

  // Formatar valor monetário
  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  // Formatar endereço completo
  private formatEnderecoCompleto(contract: Contract): string {
    const parts = [
      contract.endereco_inst,
      contract.numero_inst,
      contract.complemento_inst,
      contract.bairro_inst,
      contract.cidade_inst,
      contract.estado_inst,
      contract.cep_inst,
    ].filter(Boolean);

    return parts.join(', ');
  }
}

export const contractService = new ContractService();
export default contractService; 